import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Group as GroupIcon,
    Lock as LockIcon,
    Person as PersonIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    Select,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import folderPermissionService from '../../services/FolderPermissionService';
import groupControlService from '../../services/GroupControlService';
import userServiceInstance from '../../services/UserService';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const PERMISSION_LEVELS = [{ value: 'ADMIN', label: 'Admin' }];

const FolderPermissionsDialog = ({ open, onClose, folderPath, folderName }) => {
    useTrackUsage('FolderPermissionsDialog', 'dialog', 'folder-browser', open);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [permissionData, setPermissionData] = useState(null);
    const [inheritFromParent, setInheritFromParent] = useState(false);
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedPermission, setSelectedPermission] = useState('READ');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (open && folderPath) {
            loadPermissions();
            loadUsers();
            loadGroups();
        }
    }, [open, folderPath]);

    const loadPermissions = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await folderPermissionService.getFolderPermissions(folderPath);
            setPermissionData(data);
            setInheritFromParent(data.inheritFromParent || false);
        } catch (err) {
            console.error('Error loading permissions:', err);
            setError('Failed to load folder permissions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            const response = await userServiceInstance.getAllUsers();
            setUsers(response || []);
        } catch (err) {
            console.error('Error loading users:', err);
        }
    };

    const loadGroups = async () => {
        try {
            const groupsData = await groupControlService.getAllGroups();
            setGroups(groupsData);
        } catch (err) {
            console.error('Error loading groups:', err);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSavePermissions = async () => {
        setSaving(true);
        setError(null);
        try {
            await folderPermissionService.setInheritPermissions(folderPath, inheritFromParent);
            setSuccess('Inherit permissions updated successfully');
            loadPermissions(); // Reload to get the latest permissions
        } catch (err) {
            console.error('Error saving permissions:', err);
            setError('Failed to update permissions. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleInheritChange = (event) => {
        setInheritFromParent(event.target.checked);
    };

    const handleAddUserPermission = async () => {
        if (!selectedUser || !selectedPermission) {
            setError('Please select a user and permission level');
            return;
        }

        try {
            await folderPermissionService.setUserPermission(folderPath, selectedUser, selectedPermission);
            setSuccess('User permission added successfully');
            setSelectedUser('');
            loadPermissions();
        } catch (err) {
            console.error('Error adding user permission:', err);
            setError('Failed to add user permission. Please try again.');
        }
    };

    const handleAddGroupPermission = async () => {
        if (!selectedGroup) {
            setError('Please select a group and permission level');
            return;
        }

        try {
            await folderPermissionService.setGroupPermission(folderPath, selectedGroup, PERMISSION_LEVELS[0].value);
            setSuccess('Group permission added successfully');
            setSelectedGroup('');
            loadPermissions();
        } catch (err) {
            console.error('Error adding group permission:', err);
            setError('Failed to add group permission. Please try again.');
        }
    };

    const handleRemoveUserPermission = async (userEmail) => {
        try {
            await folderPermissionService.removeUserPermission(folderPath, userEmail);
            setSuccess('User permission removed successfully');
            loadPermissions();
        } catch (err) {
            console.error('Error removing user permission:', err);
            setError('Failed to remove user permission. Please try again.');
        }
    };

    const handleRemoveGroupPermission = async (groupId) => {
        try {
            await folderPermissionService.removeGroupPermission(folderPath, groupId);
            setSuccess('Group permission removed successfully');
            loadPermissions();
        } catch (err) {
            console.error('Error removing group permission:', err);
            setError('Failed to remove group permission. Please try again.');
        }
    };

    const renderUserPermissions = () => {
        if (!permissionData || !permissionData.userPermissions) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No user permissions found
                </Typography>
            );
        }

        const userPermissions = permissionData.userPermissions;
        const userPermissionEntries = Object.entries(userPermissions);

        if (userPermissionEntries.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No user permissions found
                </Typography>
            );
        }

        return (
            <List>
                {userPermissionEntries.map(([userEmail, permission]) => (
                    <ListItem key={userEmail}>
                        <ListItemIcon>
                            <PersonIcon />
                        </ListItemIcon>
                        <ListItemText primary={userEmail} secondary={`Permission: ${permission}`} />
                        <ListItemSecondaryAction>
                            <Tooltip title="Remove Permission">
                                <IconButton
                                    edge="end"
                                    color="error"
                                    onClick={() => handleRemoveUserPermission(userEmail)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
        );
    };

    const renderGroupPermissions = () => {
        if (!permissionData || !permissionData.groupPermissions) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No group permissions found
                </Typography>
            );
        }

        const groupPermissions = permissionData.groupPermissions;
        const groupPermissionEntries = Object.entries(groupPermissions);

        if (groupPermissionEntries.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No group permissions found
                </Typography>
            );
        }

        return (
            <List>
                {groupPermissionEntries.map(([groupId, permission]) => {
                    // Find group name from groups list
                    const group = groups.find((g) => g.id === groupId);
                    const groupName = group ? group.name : `Group ${groupId}`;
                    const isAdminGroup = groupName === 'all_vaani_admin_users';

                    return (
                        <ListItem key={groupId}>
                            <ListItemIcon>
                                <GroupIcon />
                            </ListItemIcon>
                            <ListItemText primary={groupName} secondary={`Permission: ${permission}`} />
                            <ListItemSecondaryAction>
                                {isAdminGroup ? (
                                    <Tooltip title="You are not allowed to delete this group">
                                        <span>
                                            <IconButton edge="end" color="error" disabled sx={{ opacity: 0.5 }}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Remove Permission">
                                        <IconButton
                                            edge="end"
                                            color="error"
                                            onClick={() => handleRemoveGroupPermission(groupId)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </ListItemSecondaryAction>
                        </ListItem>
                    );
                })}
            </List>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LockIcon sx={{ mr: 1 }} />
                    Folder Permissions: {folderName || folderPath}
                </Box>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                                {success}
                            </Alert>
                        )}

                        <Box sx={{ mb: 2 }}>
                            <FormControlLabel
                                control={<Checkbox checked={inheritFromParent} onChange={handleInheritChange} />}
                                label="Inherit permissions from parent folder"
                            />
                            <Typography variant="caption" color="text.secondary" display="block">
                                When enabled, this folder will inherit all permissions from its parent folder.
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                        >
                            {/*<Tab label="User Permissions" />*/}
                            <Tab label="Group Permissions" />
                        </Tabs>

                        {/*{tabValue === 1 && (
                            <>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Select
                                        value={selectedUser}
                                        onChange={(e) => setSelectedUser(e.target.value)}
                                        displayEmpty
                                        fullWidth
                                        variant="outlined"
                                        sx={{ flex: 2 }}
                                    >
                                        <MenuItem value="" disabled>
                                            Select User
                                        </MenuItem>
                                        {users.map((user) => (
                                            <MenuItem key={user.email} value={user.email}>
                                                {user.name || user.email}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    <Select
                                        value={selectedPermission}
                                        onChange={(e) => setSelectedPermission(e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        sx={{ flex: 1 }}
                                    >
                                        {PERMISSION_LEVELS.map((level) => (
                                            <MenuItem key={level.value} value={level.value}>
                                                {level.label}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddUserPermission}
                                        disabled={!selectedUser}
                                    >
                                        Add
                                    </Button>
                                </Box>

                                {renderUserPermissions()}
                            </>
                        )} */}

                        {tabValue === 0 && (
                            <>
                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <Select
                                        value={selectedGroup}
                                        onChange={(e) => setSelectedGroup(e.target.value)}
                                        displayEmpty
                                        fullWidth
                                        variant="outlined"
                                        sx={{ flex: 2 }}
                                    >
                                        <MenuItem value="" disabled>
                                            Select Group
                                        </MenuItem>
                                        {groups.map((group) => (
                                            <MenuItem key={group.name} value={group.name}>
                                                {group.name}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddGroupPermission}
                                        disabled={!selectedGroup}
                                    >
                                        Add
                                    </Button>
                                </Box>

                                {renderGroupPermissions()}
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button
                    onClick={handleSavePermissions}
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={loading || saving}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FolderPermissionsDialog;
